import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import TextMessaging from "@/components/universal/text-messaging";
import ErrorText from "@/components/universal/error-text";
import {
  useDeletePatient,
  getGetPatientQueryOptions,
} from "@/requests/gen/react-query/patient";
import { useSuspenseQuery } from "@tanstack/react-query";
import { suspenseOptions } from "@/lib/suspense-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import formatUSD from "@/lib/format-currency";
import { EditPatientDetailsModal } from "@/components/modals/edit-patient-details-modal";
import { MergePatientModal } from "@/components/modals/merge-patient-modal";
import { PatientAddressModal } from "@/components/modals/patient-address-modal";
import { PatientPayorModal } from "@/components/modals/patient-payor-modal";
import { useMemo, useState } from "react";
import { parseAsBoolean, useQueryState } from "nuqs";
import { PaymentModal } from "@/components/modals/payment-modal";
import { Button } from "@/components/ui/button";
import {
  useDeletePatientPayment,
  useMarkPatientPaymentPrimary,
} from "@/requests/gen/react-query/patient-payment";
import ConfirmModal from "@/components/modals/confirm-modal";
import { Separator } from "@/components/ui/separator";
import { format, parseISO } from "date-fns";
import { Merge, Plus, Trash2 } from "lucide-react";
import { Header } from "@/components/universal/header";
import { toast } from "sonner";
import parsePhoneNumber from "libphonenumber-js";
import PatientHistory from "./-patient-history";
import { EntityTypeEnum } from "@/requests/gen/react-query/fastAPI.schemas";
import { useGetConversation } from "@/requests/gen/react-query/text-message";
import { PatientOutcomeCampaign } from "./-patient-outcome-campaign";
import PatientIncompleteTasks from "./-patient-incomplete-tasks";

export const Route = createFileRoute(
  "/_private/_patient-360/patient/$patientId/",
)({
  loader: ({ context: { queryClient }, params: { patientId } }) =>
    queryClient.ensureQueryData(getGetPatientQueryOptions(patientId)),
  component: PatientDetailsPage,
});

function PatientDetailsPage() {
  const { patientId } = Route.useParams();
  const navigate = useNavigate();

  const {
    data: patient,
    isLoading,
    error,
    refetch,
  } = useSuspenseQuery(suspenseOptions(getGetPatientQueryOptions(patientId)));
  const { data: conversation, refetch: refetchConversation } =
    useGetConversation(
      {
        patient_ids: [patientId],
        patient_phone: patient?.phone || "",
      },
      {
        query: {
          enabled: !!patient,
        },
      },
    );

  const deletePaymentMutation = useDeletePatientPayment();
  const markPrimaryMutation = useMarkPatientPaymentPrimary();

  const [showDelete, setShowDelete] = useState<boolean>(false);
  const [showMarkPrimary, setShowMarkPrimary] = useState<boolean>(false);
  const [selectedId, setSelectedId] = useState<number>(-1);

  // patient merge modal state
  const [mergeOpen, setMergeOpen] = useQueryState(
    "merge",
    parseAsBoolean.withDefault(false),
  );

  // patient delete modal state variables
  const [showDeletePatientConfirm, setShowDeletePatientConfirm] =
    useState<boolean>(false);
  const deletePatientMutation = useDeletePatient();
  const canDeletePatient = patient?.orders.every((order) => order.is_deleted);

  const canTransferPatient = useMemo(() => {
    // Patient can be transferred if they have no orders OR all orders are deleted
    if (!patient?.orders || patient.orders.length === 0) {
      return true;
    }

    // Check if all orders are deleted
    const allOrdersDeleted = patient.orders.every((order) => order.is_deleted);

    return allOrdersDeleted;
  }, [patient?.orders]);

  const outcomesEnabled = patient?.user_entities.some(
    (ue) =>
      ue.entity.entity_type === EntityTypeEnum.Practice &&
      ue.entity.outcomes_enabled,
  );

  const primaryAddress = patient?.addresses.find((a) => a.is_primary);
  const otherAddresses = patient?.addresses.filter((a) => !a.is_primary) ?? [];
  const primaryPayor = patient?.patient_payors.find((p) => p.is_primary);
  const otherPayors =
    patient?.patient_payors.filter((p) => !p.is_primary) ?? [];
  const primaryPayment = patient?.payments.find((p) => p.is_primary);
  const otherPayments = patient?.payments.filter((p) => !p.is_primary) ?? [];

  function RenderDetails(
    title: string,
    items: { label: string; value: string | undefined | null }[],
    action?: React.ReactNode,
  ) {
    return (
      <div>
        <div className="flex justify-between items-center">
          <h4 className="text-lg font-bold">{title}</h4>
        </div>

        {isLoading ? (
          <p>Loading...</p>
        ) : (
          <div>
            {items.map((item, index) =>
              item.value ? (
                <p key={index} className="text-muted-foreground text-sm">
                  <span>{item.label}</span>
                  <span className="font-semibold">{item.value}</span>
                </p>
              ) : null,
            )}
            {action}
          </div>
        )}
      </div>
    );
  }

  const deletePayment = (patientPaymentId: number) => {
    deletePaymentMutation.mutate(
      { patientPaymentId },
      {
        onSuccess: () => {
          refetch();
          setSelectedId(-1);
        },

        onError: (error) => {
          console.error(error);
          setSelectedId(-1);
        },
      },
    );
  };

  const handleShowDelete = (paymentId?: number) => {
    setShowDelete(!showDelete);
    setSelectedId(paymentId ?? -1);
  };

  const handleShowMarkPrimary = (paymentId?: number) => {
    setShowMarkPrimary(!showMarkPrimary);
    setSelectedId(paymentId ?? -1);
  };

  // patient delete handler
  const handleDeletePatient = () => {
    deletePatientMutation.mutate(
      {
        patientId,
      },
      {
        onSuccess: () => {
          toast.success("Patient successfully deleted.");
          navigate({ to: "/patient/lookup" });
        },
        onError: (error) => {
          toast.error("Failed to delete patient. Please try again.");
          console.error(error);
        },
      },
    );
  };

  return (
    <div>
      <title>{`${patient?.last_name}, ${patient?.first_name} - Legrande Health Portal`}</title>
      <Header
        crumbs={[
          { name: "Patients", link: { to: "/patient/lookup" } },
          {
            name: `${patient?.last_name}, ${patient?.first_name}`,
            link: {
              to: "/patient/$patientId",
              params: { patientId },
            },
          },
        ]}
      >
        <Button
          variant="ghost"
          size="icon"
          tooltip="Merge Patient"
          onClick={() => setMergeOpen(true)}
        >
          <Merge />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          tooltip={
            canDeletePatient
              ? "Delete Patient"
              : "Cannot delete patient with existing orders"
          }
          disabled={!canDeletePatient}
          onClick={() => setShowDeletePatientConfirm(true)}
        >
          <Trash2 />
        </Button>
      </Header>
      {/* The confirm modal that pops up when trying to delete a payment */}
      <ConfirmModal
        isOpen={showDelete}
        text="Are you sure you want to delete this payment?"
        title="Delete Payment"
        onCancel={() => {
          handleShowDelete();
        }}
        onConfirm={() => {
          deletePayment(selectedId);
          setShowDelete(false);
        }}
        img="delete"
        cancelText="Cancel"
        confirmText="Delete"
      />
      {/* The confirm modal that pops up when trying to mark a payment as primary */}
      <ConfirmModal
        isOpen={showMarkPrimary}
        text="Marking this payment method as primary will update the payment
              information on all existing orders with a status of 'Draft' or
              'Open' that have not yet been charged. Orders that already
              have a charged shipment will not be changed."
        title="Mark Payment as Primary"
        onCancel={() => {
          handleShowMarkPrimary();
        }}
        onConfirm={() => {
          markPrimaryMutation.mutate(
            { patientPaymentId: selectedId },
            {
              onSuccess: () => {
                refetch();
                setSelectedId(-1);
                toast.success("Payment method marked as primary.");
              },
              onError: (error) => {
                console.error(error);
                setSelectedId(-1);
                toast.error(
                  "Failed to mark payment as primary. Please try again.",
                );
              },
            },
          );
          setShowMarkPrimary(false);
        }}
        img="warning"
        cancelText="Cancel"
        confirmText="Mark Primary"
      />
      {/* This confirm modal pops up when trying to delete a patient */}
      <ConfirmModal
        isOpen={showDeletePatientConfirm}
        text="Are you sure you want to delete this patient?"
        title="Delete Patient"
        onCancel={() => setShowDeletePatientConfirm(false)}
        onConfirm={() => {
          handleDeletePatient();
          setShowDeletePatientConfirm(false);
        }}
        img="delete"
        cancelText="Cancel"
        confirmText="Delete Patient"
      />

      {patient && (
        <MergePatientModal
          isOpen={mergeOpen}
          onClose={() => setMergeOpen(null)}
          currentPatient={patient}
          onMergeComplete={(survivingPatientId) => {
            toast.success("Patients merged successfully.");
            if (survivingPatientId !== patientId) {
              navigate({
                to: "/patient/$patientId",
                params: { patientId: survivingPatientId },
              });
            } else {
              refetch();
            }
          }}
        />
      )}

      {/* Patient Details Section */}
      <Card>
        <CardHeader>
          <CardTitle>Patient Details</CardTitle>
          <CardDescription>Review the patient details below.</CardDescription>
          <CardAction>
            <Button variant="default" size="sm" asChild>
              <Link to="/order/create" search={{ patientId }}>
                <Plus />
                Create Order
              </Link>
            </Button>
          </CardAction>
          {error && <ErrorText text="Failed to load patient details" />}
        </CardHeader>
        <CardContent>
          {patient && (
            // Only render the main section if the patient is loaded
            <main className="flex flex-1 flex-col gap-4 md:gap-8">
              <div className="grid gap-4 md:grid-cols-2 md:gap-8 lg:grid-cols-4">
                {/* Details Card */}
                {RenderDetails(
                  "Details",
                  [
                    {
                      label: "Name: ",
                      value: `${patient.last_name}, ${patient.first_name}`,
                    },
                    {
                      label: "Practice: ",
                      value: patient.user_entities[0].entity.name,
                    },
                    {
                      label: "DOB: ",
                      value: format(
                        parseISO(patient.date_of_birth),
                        "MM-dd-yyyy",
                      ),
                    },
                    { label: "Email: ", value: patient.email },
                    {
                      label: "Phone: ",
                      value: parsePhoneNumber(patient.phone)?.formatNational(),
                    },
                    { label: "Birth Sex: ", value: patient.sex || "Unknown" },
                    {
                      label: "Allergies: ",
                      value: patient.allergies
                        ? patient.allergies.join(", ")
                        : "None",
                    },
                  ],
                  <EditPatientDetailsModal
                    triggerText="Edit Details"
                    patientDetails={patient}
                    onCreate={() => refetch()}
                    canTransferPatient={canTransferPatient}
                  />,
                )}

                {/* Shipping Card */}
                {RenderDetails(
                  "Shipping",
                  primaryAddress
                    ? [
                        { label: "", value: primaryAddress.st_address_1 },
                        { label: "", value: primaryAddress.st_address_2 },
                        {
                          label: "",
                          value: `${primaryAddress.city || ""}, ${primaryAddress.state_province || ""} ${primaryAddress.postal_code || ""}`,
                        },
                        {
                          label: "",
                          value: primaryAddress.country || "",
                        },
                      ]
                    : [{ label: "", value: "No primary address found" }],

                  primaryAddress ? (
                    <PatientAddressModal
                      triggerText="Edit Address"
                      patientAddress={primaryAddress}
                      patientId={patient.patient_id}
                      onCreate={() => refetch()}
                    />
                  ) : (
                    <PatientAddressModal
                      triggerText="Add Address"
                      patientId={patient.patient_id}
                      onCreate={() => refetch()}
                    />
                  ),
                )}

                {/* Payor Card */}
                {RenderDetails(
                  "Payor",
                  primaryPayor
                    ? [
                        {
                          label: "Type: ",
                          value: primaryPayor?.payor.payor_type,
                        },
                        {
                          label: "Provider: ",
                          value: primaryPayor?.payor.name,
                        },
                        {
                          label: "Account #: ",
                          value: primaryPayor?.account_number,
                        },
                        {
                          label: "Group #: ",
                          value: primaryPayor?.group_number,
                        },
                      ]
                    : [{ label: "", value: "No primary payor found" }],

                  primaryPayor ? (
                    <PatientPayorModal
                      triggerText="Edit Payor"
                      onCreate={() => refetch()}
                      patientId={patient.patient_id}
                      patientPayor={primaryPayor}
                    />
                  ) : (
                    <PatientPayorModal
                      triggerText="Add Payor"
                      onCreate={() => refetch()}
                      patientId={patient.patient_id}
                    />
                  ),
                )}

                {/* Payment Card */}
                {RenderDetails(
                  "Payment",
                  primaryPayment
                    ? [
                        { label: "Brand: ", value: primaryPayment?.brand },
                        {
                          label: "Card #: ",
                          value: primaryPayment?.masked_number,
                        },
                        {
                          label: "Exp: ",
                          value: format(parseISO(primaryPayment?.exp), "MM/yy"),
                        },
                        {
                          label: "Billing: ",
                          value: primaryPayment?.st_address_1,
                        },
                      ]
                    : [{ label: "", value: "No primary payment found" }],

                  primaryPayment ? (
                    <p className="flex gap-3 my-3">
                      <PaymentModal
                        triggerText="Add Payment"
                        patientId={patient.patient_id}
                        onCreate={() => {
                          refetch();
                          refetchConversation();
                        }}
                      />
                      <Button
                        variant="link"
                        className="p-0 h-1 text-destructive"
                        onClick={() => {
                          handleShowDelete(primaryPayment.patient_payment_id);
                        }}
                      >
                        Delete
                      </Button>
                    </p>
                  ) : (
                    <PaymentModal
                      triggerText="Add Payment"
                      patientId={patient.patient_id}
                      onCreate={() => {
                        refetch();
                        refetchConversation();
                      }}
                    />
                  ),
                )}
              </div>

              {/* Display other addresses */}
              {otherAddresses.length > 0 && (
                <div className="flex flex-col">
                  <Accordion type="single" collapsible>
                    <AccordionItem value="item-1">
                      <AccordionTrigger>
                        Other Addresses on File
                      </AccordionTrigger>
                      <AccordionContent>
                        <div className="flex flex-wrap gap-3">
                          {otherAddresses.map((address, index) => (
                            <div key={index} className="flex">
                              <div className="flex flex-col">
                                {RenderDetails(
                                  ``,
                                  [
                                    { label: "", value: address.st_address_1 },
                                    { label: "", value: address.st_address_2 },
                                    {
                                      label: "",
                                      value: `${address.city || ""}, ${address.state_province || ""} ${address.postal_code || ""}`,
                                    },
                                    {
                                      label: "",
                                      value: `${address.country || ""}`,
                                    },
                                  ],
                                  <PatientAddressModal
                                    triggerText="Edit Address"
                                    patientAddress={otherAddresses[index]}
                                    patientId={patient.patient_id}
                                    onCreate={() => refetch()}
                                  />,
                                )}
                              </div>
                              <Separator
                                orientation="vertical"
                                className="ml-4 mr-2 hidden sm:block"
                              />
                            </div>
                          ))}
                          <div className="flex items-center min-h-3">
                            <PatientAddressModal
                              triggerText="Add Address"
                              patientId={patient.patient_id}
                              onCreate={() => refetch()}
                            />
                          </div>
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                </div>
              )}

              {/* Display other Payors */}
              {otherPayors.length > 0 && (
                <div className="flex flex-col">
                  <Accordion type="single" collapsible>
                    <AccordionItem value="item-1">
                      <AccordionTrigger>Other Payors on File</AccordionTrigger>
                      <AccordionContent>
                        <div className="flex gap-3">
                          {otherPayors.map((payor, index) => (
                            <div key={index} className="flex">
                              {RenderDetails(
                                ``,
                                [
                                  {
                                    label: "Type: ",
                                    value: payor.payor.payor_type,
                                  },
                                  {
                                    label: "Provider: ",
                                    value: payor.payor.name,
                                  },
                                  {
                                    label: "Account #: ",
                                    value: payor.account_number,
                                  },
                                  {
                                    label: "Group #: ",
                                    value: payor.group_number,
                                  },
                                ],
                                <PatientPayorModal
                                  triggerText="Edit Payor"
                                  onCreate={() => refetch()}
                                  patientId={patient.patient_id}
                                  patientPayor={otherPayors[index]}
                                />,
                              )}
                              <Separator
                                orientation="vertical"
                                className="ml-4 mr-2 hidden sm:block"
                              />
                            </div>
                          ))}
                          <div className="flex items-center min-h-3">
                            <PatientPayorModal
                              triggerText="Add Payor"
                              onCreate={() => refetch()}
                              patientId={patient.patient_id}
                            />
                          </div>
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                </div>
              )}

              {/* Display other Payments */}
              {otherPayments.length > 0 && (
                <div className="flex flex-col">
                  <Accordion type="single" collapsible>
                    <AccordionItem value="item-1">
                      <AccordionTrigger>
                        Other Payments on File
                      </AccordionTrigger>
                      <AccordionContent>
                        <div className="flex gap-3">
                          {otherPayments.map((payment, index) => (
                            <div key={index} className="flex">
                              {RenderDetails(
                                ``,
                                [
                                  { label: "Brand: ", value: payment.brand },
                                  {
                                    label: "Card #: ",
                                    value: payment.masked_number,
                                  },
                                  { label: "Exp: ", value: payment.exp },
                                  {
                                    label: "Billing: ",
                                    value: payment.st_address_1,
                                  },
                                ],
                                <span className="flex gap-4">
                                  <Button
                                    variant="link"
                                    className="p-0 h-4 text-destructive"
                                    onClick={() => {
                                      handleShowDelete(
                                        otherPayments[index].patient_payment_id,
                                      );
                                    }}
                                  >
                                    Delete
                                  </Button>
                                  <Button
                                    variant="link"
                                    className="p-0 h-4 text-primary"
                                    onClick={() => {
                                      handleShowMarkPrimary(
                                        otherPayments[index].patient_payment_id,
                                      );
                                    }}
                                  >
                                    Mark Primary
                                  </Button>
                                </span>,
                              )}
                              <Separator
                                orientation="vertical"
                                className="ml-4 mr-2 hidden sm:block"
                              />
                            </div>
                          ))}
                          <div className="flex items-center min-h-3">
                            <PaymentModal
                              triggerText="Add Payment"
                              patientId={patient.patient_id}
                              onCreate={() => {
                                refetch();
                                refetchConversation();
                              }}
                            />
                          </div>
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                </div>
              )}
            </main>
          )}
        </CardContent>
      </Card>
      {/* Display Incomplete Tasks */}
      {patient && (
        <PatientIncompleteTasks refetch={refetch} patient={patient} />
      )}

      {/* 1-Click Outcomes campaign */}
      {patient && outcomesEnabled && (
        <PatientOutcomeCampaign patientId={patient.patient_id} />
      )}

      {/* Communication, Orders, Call Notes (History) */}
      <Tabs defaultValue="communication" className="mt-4">
        <TabsList>
          <TabsTrigger value="communication">Communication</TabsTrigger>
          <TabsTrigger value="orders">Orders</TabsTrigger>
        </TabsList>
        <TabsContent value="communication">
          {/* Text Messaging Section */}
          <div className="flex flex-col lg:flex-row w-full gap-4 lg:h-164">
            <PatientHistory patientId={patientId} />
            <Card className="w-full h-128 lg:h-full gap-0">
              <TextMessaging
                patient_phone={patient?.phone}
                patients={
                  patient
                    ? [patient].map((p) => ({
                        ...p,
                        user_id: p.patient_id,
                      }))
                    : []
                }
                practice={patient?.user_entities[0].entity}
                conversation={conversation}
                texting_enabled={patient?.texting_unsubscribed_at === null}
                refetch={refetchConversation}
              />
            </Card>
          </div>
        </TabsContent>
        <TabsContent value="orders">
          {/* Orders Section */}
          <Card>
            <CardHeader>
              <CardTitle>Order History</CardTitle>
              <CardDescription>
                View the patient's order history below.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {patient && patient.orders && patient.orders.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Order ID</TableHead>
                      <TableHead>Total</TableHead>
                      <TableHead>Created At</TableHead>
                      {/* <TableHead>Tracking #</TableHead> */}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {patient.orders
                      .slice()
                      .reverse()
                      .map((order) => (
                        <TableRow
                          key={order.order_id}
                          onClick={() =>
                            navigate({
                              to: "/order/$orderId",
                              params: { orderId: String(order.order_id) },
                            })
                          }
                          className="cursor-pointer"
                        >
                          <TableCell>{order.order_id}</TableCell>
                          <TableCell>{formatUSD(order.total_price)}</TableCell>
                          <TableCell>
                            {format(parseISO(order.created_at), "MM-dd-yyyy")}
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              ) : (
                <p>No orders found for this patient.</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
